import React from "react";
import { fireEvent, render, screen, waitFor } from "@testing-library/react";
import "@testing-library/jest-dom";
import PDVCompanyConnections from "./PDVCompanyConnections";
import type { PDVCompanyConnections as PDVResult } from "../types";

const mockFetchConnections = jest.fn();

jest.mock("../hooks", () => ({
  useBKGPDVCompanyConnections: () => mockFetchConnections,
}));

const renderPanel = () =>
  render(
    <PDVCompanyConnections workspaceId="ws-1" companyRef="ws-1/company/acme" />,
  );

const exactResult = (overrides: Partial<PDVResult> = {}): PDVResult => ({
  match_status: "exact",
  match_method: "domain",
  company_name: "Example Therapeutics",
  company_domain: "example.test",
  candidates: [],
  connections: [
    {
      ssi_employee_name: "Sven Internal",
      contact_name: "Ada Ext",
      contact_job_title: "Chief Scientific Officer",
      relationship_types: ["EMAIL_INTERACTION", "MEETING"],
    },
  ],
  ...overrides,
});

// Deliberately not the real wording: the panel must show whatever PDV sends.
const pdvDefinition = {
  summary: "PDV SUMMARY TEXT",
  bands: [
    { label: "strong" as const, range: "above 80", example: "STRONG EXAMPLE" },
    { label: "medium" as const, range: "50 to 80", example: "MEDIUM EXAMPLE" },
    { label: "weak" as const, range: "below 50", example: null },
  ],
};

beforeEach(() => {
  mockFetchConnections.mockReset();
});

describe("PDVCompanyConnections", () => {
  it("shows a loading state while the lookup is in flight", () => {
    mockFetchConnections.mockReturnValue(new Promise(() => {}));
    renderPanel();
    expect(screen.getByText("Checking SSI connections…")).toBeInTheDocument();
  });

  it("renders each connection and the matched PDV company", async () => {
    mockFetchConnections.mockResolvedValue(exactResult());
    renderPanel();

    expect(
      await screen.findByText("Sven Internal knows Ada Ext"),
    ).toBeInTheDocument();
    expect(screen.getByText("Chief Scientific Officer")).toBeInTheDocument();
    expect(screen.getByText("email interaction · meeting")).toBeInTheDocument();
    expect(
      screen.getByText("Matched in PDV: Example Therapeutics · example.test"),
    ).toBeInTheDocument();
    expect(screen.queryByText("Not scored")).not.toBeInTheDocument();
  });

  it("shows only the total until the connection list is opened", async () => {
    const connection = exactResult().connections[0];
    mockFetchConnections.mockResolvedValue(
      exactResult({
        connections: [
          connection,
          { ...connection, contact_name: "Bea Ext" },
          { ...connection, contact_name: "Cy Ext" },
        ],
      }),
    );
    renderPanel();

    const summary = await screen.findByRole("button", {
      name: "3 SSI connections",
    });
    expect(summary).toHaveAttribute("aria-expanded", "false");

    fireEvent.click(summary);
    expect(summary).toHaveAttribute("aria-expanded", "true");
    expect(screen.getByText("Sven Internal knows Cy Ext")).toBeVisible();
  });

  it("counts a single connection in the singular", async () => {
    mockFetchConnections.mockResolvedValue(exactResult());
    renderPanel();

    expect(
      await screen.findByRole("button", { name: "1 SSI connection" }),
    ).toBeInTheDocument();
  });

  it("has no connection list to open when there are no connections", async () => {
    mockFetchConnections.mockResolvedValue(exactResult({ connections: [] }));
    renderPanel();

    await screen.findByText(
      "No known SSI connections were found for this company.",
    );
    expect(
      screen.queryByTestId("pdv-connections-accordion"),
    ).not.toBeInTheDocument();
  });

  it("renders parent-domain matches without dropping their connections", async () => {
    mockFetchConnections.mockResolvedValue(
      exactResult({ match_status: "parent_domain" }),
    );
    renderPanel();

    expect(
      await screen.findByText(
        "PDV matched this company using its parent domain: Example Therapeutics · example.test.",
      ),
    ).toBeInTheDocument();
    expect(screen.getByText("Sven Internal knows Ada Ext")).toBeInTheDocument();
  });

  it("explains an ambiguous match instead of guessing", async () => {
    mockFetchConnections.mockResolvedValue(
      exactResult({ match_status: "ambiguous", connections: [] }),
    );
    renderPanel();

    expect(
      await screen.findByText(
        /could not be matched reliably, so no relationship data is shown/,
      ),
    ).toBeInTheDocument();
  });

  it("reports when no match was found", async () => {
    mockFetchConnections.mockResolvedValue(
      exactResult({ match_status: "none", connections: [] }),
    );
    renderPanel();

    expect(
      await screen.findByText("No reliable match was found in PDV."),
    ).toBeInTheDocument();
  });

  it("distinguishes a matched company with no known connections", async () => {
    mockFetchConnections.mockResolvedValue(exactResult({ connections: [] }));
    renderPanel();

    expect(
      await screen.findByText(
        "No known SSI connections were found for this company.",
      ),
    ).toBeInTheDocument();
  });

  it("degrades quietly when PDV is unavailable", async () => {
    mockFetchConnections.mockRejectedValue(new Error("503"));
    renderPanel();

    expect(
      await screen.findByText("SSI connections are temporarily unavailable."),
    ).toBeInTheDocument();
  });

  it("shows actionable configuration failures instead of calling them temporary", async () => {
    mockFetchConnections.mockRejectedValue({
      status: 501,
      response: {
        detail: {
          code: "pdv_credential_not_deposited",
          message: "server-provided text is not trusted by the component",
        },
      },
    });
    renderPanel();

    expect(
      await screen.findByText(
        "SSI connections have not been provisioned for this environment yet.",
      ),
    ).toBeInTheDocument();
    expect(
      screen.queryByText("SSI connections are temporarily unavailable."),
    ).not.toBeInTheDocument();
  });

  // Every refusal the backend's environment gate can emit
  // (api/core/services/pdv_environment.py::_reason_code) is permanent for the
  // deployment. None of them may fall through to the transient fallback.
  it.each([
    "pdv_environment_not_permitted",
    "pdv_environment_unidentified",
    "pdv_environment_forbidden_site",
    "pdv_environment_identity_mismatch",
  ])(
    "reports %s as unavailable here, not as a passing outage",
    async (code) => {
      mockFetchConnections.mockRejectedValue({
        status: 403,
        response: { detail: { code, message: "ignored" } },
      });
      renderPanel();

      expect(
        await screen.findByText(
          "SSI connections are not available in this environment.",
        ),
      ).toBeInTheDocument();
      expect(
        screen.queryByText("SSI connections are temporarily unavailable."),
      ).not.toBeInTheDocument();
    },
  );

  it("labels each connection with its strength band and explains the bands once", async () => {
    mockFetchConnections.mockResolvedValue(
      exactResult({
        connections: [
          {
            ssi_employee_name: "Sven Internal",
            contact_name: "Ada Ext",
            contact_job_title: "CSO",
            relationship_types: ["MEETING"],
            strength_label: "strong",
          },
          {
            ssi_employee_name: "Tove Internal",
            contact_name: "Bo Ext",
            relationship_types: ["EMAIL_INTERACTION"],
            strength_label: "medium",
          },
          {
            ssi_employee_name: "Uma Internal",
            contact_name: "Cy Ext",
            relationship_types: ["CALL_INTERACTION"],
            strength_label: "weak",
          },
          {
            ssi_employee_name: "Vera Internal",
            contact_name: "Di Ext",
            relationship_types: ["LINKEDIN_CONNECTION"],
            strength_label: null,
          },
        ],
        strength_definition: pdvDefinition,
      }),
    );
    renderPanel();

    expect(await screen.findByText("Strong")).toBeInTheDocument();
    expect(screen.getByText("Medium")).toBeInTheDocument();
    expect(screen.getByText("Weak")).toBeInTheDocument();
    expect(screen.getByText("Vera Internal knows Di Ext")).toBeInTheDocument();
    expect(screen.queryByText(/not scored/i)).not.toBeInTheDocument();
    const infoButtons = screen.getAllByRole("button", {
      name: "How strength is measured",
    });
    expect(infoButtons).toHaveLength(1);

    fireEvent.mouseEnter(infoButtons[0]);
    expect(await screen.findByText("PDV SUMMARY TEXT")).toBeInTheDocument();
    expect(screen.getByText(/above 80.*STRONG EXAMPLE/)).toBeInTheDocument();
    expect(screen.getByText(/50 to 80.*MEDIUM EXAMPLE/)).toBeInTheDocument();
    expect(screen.getByText(/below 50/)).toBeInTheDocument();
  });

  it("shows the chips but no explanation button when PDV's definition is missing", async () => {
    mockFetchConnections.mockResolvedValue(
      exactResult({
        connections: [
          {
            ssi_employee_name: "Sven Internal",
            contact_name: "Ada Ext",
            relationship_types: ["MEETING"],
            strength_label: "strong",
          },
        ],
        strength_definition: null,
      }),
    );
    renderPanel();

    expect(await screen.findByText("Strong")).toBeInTheDocument();
    expect(
      screen.queryByRole("button", { name: "How strength is measured" }),
    ).not.toBeInTheDocument();
  });

  it("shows no strength chip, placeholder or explanation when PDV sends no bands", async () => {
    mockFetchConnections.mockResolvedValue(exactResult());
    renderPanel();

    expect(
      await screen.findByText("Sven Internal knows Ada Ext"),
    ).toBeInTheDocument();
    expect(screen.queryByText("Strong")).not.toBeInTheDocument();
    expect(screen.queryByText("Medium")).not.toBeInTheDocument();
    expect(screen.queryByText("Weak")).not.toBeInTheDocument();
    expect(screen.queryByText(/not scored/i)).not.toBeInTheDocument();
    expect(
      screen.queryByRole("button", { name: "How strength is measured" }),
    ).not.toBeInTheDocument();
  });

  it("ignores a strength value it does not know instead of rendering it", async () => {
    mockFetchConnections.mockResolvedValue(
      exactResult({
        connections: [
          {
            ssi_employee_name: "Sven Internal",
            contact_name: "Ada Ext",
            relationship_types: ["MEETING"],
            strength_label: "legendary" as any,
          },
        ],
      }),
    );
    renderPanel();

    expect(
      await screen.findByText("Sven Internal knows Ada Ext"),
    ).toBeInTheDocument();
    expect(screen.queryByText(/legendary/i)).not.toBeInTheDocument();
    expect(
      screen.queryByRole("button", { name: "How strength is measured" }),
    ).not.toBeInTheDocument();
  });

  it("says PDV is switched off rather than temporarily unavailable", async () => {
    mockFetchConnections.mockRejectedValue({
      status: 403,
      response: { detail: { code: "pdv_disabled", message: "ignored" } },
    });
    renderPanel();

    expect(
      await screen.findByText(
        "SSI connections are switched off in this environment.",
      ),
    ).toBeInTheDocument();
    expect(
      screen.queryByText("SSI connections are temporarily unavailable."),
    ).not.toBeInTheDocument();
  });

  it("does not leave a stale result rendered when the company changes", async () => {
    mockFetchConnections.mockResolvedValue(exactResult());
    const { rerender } = renderPanel();
    await screen.findByText("Sven Internal knows Ada Ext");

    mockFetchConnections.mockResolvedValue(
      exactResult({
        connections: [
          {
            ssi_employee_name: "Other Colleague",
            contact_name: "Bo Second",
            contact_job_title: null,
            relationship_types: ["MEETING"],
          },
        ],
      }),
    );
    rerender(
      <PDVCompanyConnections
        workspaceId="ws-1"
        companyRef="ws-1/company/other"
      />,
    );

    await waitFor(() =>
      expect(
        screen.queryByText("Sven Internal knows Ada Ext"),
      ).not.toBeInTheDocument(),
    );
    expect(
      screen.getByText("Other Colleague knows Bo Second"),
    ).toBeInTheDocument();
  });
});
